// ============================================================================
// SAMPLE SERVICE HOOKS (Reference Implementation)
// ============================================================================
// React hooks for Sample Service Master API operations

import { useState, useEffect, useCallback } from 'react';
import { sampleServiceService } from 'api/services';
import type {
  SampleServiceDto,
  CreateSampleServiceRequest,
  UpdateSampleServiceRequest,
  SampleServiceQueryParams,
  ID,
} from 'api/types';
import type { PaginationMeta } from 'api/types';

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch paginated services
 */
export const useSampleServices = (params?: SampleServiceQueryParams) => {
  const [data, setData] = useState<SampleServiceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);

  // Serialize params to avoid stale closure / infinite re-render
  const paramsKey = JSON.stringify(params);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return { data, loading, error, pagination, refetch: fetchServices };
};

/**
 * Hook to create a service
 */
export const useCreateSampleService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createSampleService = async (
    data: CreateSampleServiceRequest,
    options?: {
      onSuccess?: (service: SampleServiceDto) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { createSampleService, loading, error };
};

/**
 * Hook to update a service
 */
export const useUpdateSampleService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateSampleService = async (
    id: ID,
    data: UpdateSampleServiceRequest,
    options?: {
      onSuccess?: (service: SampleServiceDto) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await sampleServiceService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { updateSampleService, loading, error };
};

/**
 * Hook to delete a service
 */
export const useDeleteSampleService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteSampleService = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await sampleServiceService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { deleteSampleService, loading, error };
};
